/**
 * Normalizes a URL by adding https:// protocol if missing and validating format
 * @param rawUrl - The raw URL string to normalize
 * @returns The normalized URL string or null if invalid
 */
export function normalizeUrl(rawUrl: string): string | null {
  let url = rawUrl.trim();
  // Schemes are case-insensitive (RFC 3986), so test against a lowercased copy while preserving
  // the original case of the path/query in `url`.
  const lowerUrl = url.toLowerCase();

  // Reject malformed protocols explicitly
  if (lowerUrl.startsWith('https//') || lowerUrl.startsWith('http//')) {
    return null;
  }

  // Add https:// if no protocol
  if (!lowerUrl.startsWith('http://') && !lowerUrl.startsWith('https://')) {
    url = `https://${url}`;
  }

  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname;

    // Reject IPs and localhost
    const isIP = /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname);
    const isLocalhost = hostname === 'localhost';
    if (!hostname.includes('.') || isIP || isLocalhost) {
      return null;
    }

    // Strip embedded credentials (`user:pass@host`) so they're never displayed or opened —
    // a `user:pass@` prefix on a real domain is a classic phishing display artifact.
    parsed.username = '';
    parsed.password = '';

    // Remove trailing slash (unless root)
    if (parsed.pathname !== '/' && parsed.pathname.endsWith('/')) {
      parsed.pathname = parsed.pathname.slice(0, -1);
    }

    return parsed.toString();
  } catch {
    return null;
  }
}

/**
 * Reports whether a URL carries userinfo (`user:pass@host`). Delegates to the URL parser rather than
 * scanning for `@`, which appears legitimately in paths (`/@handle`) and query strings.
 */
export function hasUrlCredentials(rawUrl: string): boolean {
  const url = rawUrl.trim();
  const withScheme = /^https?:\/\//i.test(url) ? url : `https://${url}`;

  try {
    const { username, password } = new URL(withScheme);

    return username !== '' || password !== '';
  } catch {
    return false;
  }
}

/**
 * Checks if a URL belongs to internal domains
 * @param uri - The URL to check
 * @param allowListedDomains - Optional array of allowed domains from API. Falls back to default Zillow domains if not provided.
 */
export function isInternalUrl(uri: string | null | undefined, allowListedDomains?: Array<string>): boolean {
  if (!uri) {
    return false;
  }

  try {
    const url = new URL(uri);
    const { hostname } = url;

    // Use provided domains or fall back to default internal domains
    const internalDomains = allowListedDomains || ['zillow.com', 'trulia.com', 'hotpads.com'];

    // Normalize domains to lowercase for case-insensitive comparison
    const normalizedDomains = internalDomains.map((domain) => domain.toLowerCase());

    // Check if hostname is exactly one of our domains or a valid subdomain
    return normalizedDomains.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));
  } catch {
    return false;
  }
}

/**
 * Extracts the authority substring from a URL string without parsing it, so the result is what a
 * reader actually sees — userinfo, percent-encoding, unicode and all.
 */
function extractDisplayedAuthority(rawUrl: string): string {
  const afterScheme = rawUrl
    .trim()
    // Require the `//` — without it `example.com:8080/path` parses its own host as a scheme.
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
    // WHATWG tolerates extra leading slashes, and treats `\` as a slash for special schemes.
    .replace(/^[/\\]*/, '');

  const authorityEnd = afterScheme.search(/[/\\?#]/);

  return authorityEnd === -1 ? afterScheme : afterScheme.slice(0, authorityEnd);
}

/**
 * Returns the host `url` actually opens when `displayText` spells out a different one, and null when
 * the two agree.
 *
 * Neither form is safe to show alone: unparsed text hides an internationalized-domain homograph
 * (`zillоw.com` with a Cyrillic `о` opens `xn--zillw-mye.com`), while a parsed host hides
 * percent-encoding (`example%2Ecom` parses to `example.com`). Showing the parsed host alongside the
 * unparsed text is what covers both.
 */
export function getUndisclosedHost(url: string, displayText: string): string | null {
  // Required rather than optional: a caller that has no displayed text to compare has nothing to
  // disclose against, and silently returning null there is the fail-open this helper exists to close.
  if (!displayText) {
    return null;
  }

  try {
    const { hostname } = new URL(url);
    const authority = extractDisplayedAuthority(displayText);

    // Userinfo left in the displayed text is itself the spoof — the eye stops at `www.zillow.com@`
    // and never reaches the host. Name the destination even though the two agree once parsed, so
    // this disclosure doesn't depend on every caller having stripped credentials correctly.
    if (authority.includes('@')) {
      return hostname;
    }

    // Ports aren't part of host identity, and host case is insignificant.
    const displayedHost = authority.replace(/:\d*$/, '').toLowerCase();

    return displayedHost === hostname.toLowerCase() ? null : hostname;
  } catch {
    return null;
  }
}
